
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';
import IncludeUnappliedToggle from '@/components/IncludeUnappliedToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeUnappliedToggle,
	},
})
export default class SupplierOverdueNumberDisplayer extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['suppliers', 'agents', 'dateAsOf'];

	public amount: number = 0;
	public amountNum: string = '';

	public thousandFormatter(x: number) {
		return gdbx.currencySymbol + ' ' + Math.ceil(x).toLocaleString('en-US');
	}

	public get includeUnapplied() {
		return settingx.now.includeUnapplied;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedAgents, selectedSuppliers, includeUnapplied } = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedSuppliers,
			includeUnapplied,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const table = getTableName('supplierTrades');

		let supplierFilter = '';
		if (this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((c) => `'${escapeStr(c)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}
		const baseFilter = `${supplierFilter}${agentFilter}`;

		const aggregationSql = `
			SELECT
				SUM(CASE WHEN "DOCTYPE" IN ('PI','SD') THEN TRY_CAST("LOCALOSAMOUNT" AS DOUBLE) ELSE 0 END) as os_amt,
				SUM(CASE WHEN "DOCTYPE" IN ('SC','ST','SI','SP') THEN TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE) ELSE 0 END) as minus_unapplied,
				SUM(CASE WHEN "DOCTYPE" = 'SF' THEN TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE) ELSE 0 END) as plus_unapplied
		`;

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['supplierTrades']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const [dueResult, osResult] = await Promise.all([
			queryHead.rawQuery(`
				${aggregationSql}
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedAsOfDate}
					AND TRY_CAST("DUEDATE" AS BIGINT) <= ${this.selectedAsOfDate}
					${baseFilter}
			`),
			queryHead.rawQuery(`
				${aggregationSql}
				FROM ${table}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedAsOfDate}
					${baseFilter}
			`),
		]);
		this.checkInterrupted(jobId);

		const dueOsAmount = Number(dueResult[0] && dueResult[0].os_amt) || 0;
		const osAmount = Number(osResult[0] && osResult[0].os_amt) || 0;

		let totalDue: number;
		let totalOutstanding: number;

		if (this.includeUnapplied) {
			const dueMinusUnapplied = Number(dueResult[0] && dueResult[0].minus_unapplied) || 0;
			const duePlusUnapplied = Number(dueResult[0] && dueResult[0].plus_unapplied) || 0;
			const minusUnapplied = Number(osResult[0] && osResult[0].minus_unapplied) || 0;
			const plusUnapplied = Number(osResult[0] && osResult[0].plus_unapplied) || 0;
			totalDue = dueOsAmount - dueMinusUnapplied + duePlusUnapplied;
			totalOutstanding = osAmount - minusUnapplied + plusUnapplied;
		} else {
			totalDue = dueOsAmount;
			totalOutstanding = osAmount;
		}

		this.amount = totalDue * 100 / totalOutstanding;
		this.amountNum = `${this.thousandFormatter(totalDue)} / ${this.thousandFormatter(totalOutstanding)}`;

		this.generateLoadingText(1);
		this.saveHistory('amount', 'amountNum');
	}
}
